import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { OrdersDataViewContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import exportOrders from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/ExportOrders";
import reorder from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/Reorder";
import selectOrders from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/SelectOrders";
import updateSearchFields from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import Button, {
    ButtonIcon,
    ButtonIconPresentationProps,
    ButtonPresentationProps,
    ButtonWrapper,
} from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import { ClickableProps } from "@insite/mobius/Clickable";
import DataTable, { DataTableProps, SortOrderOptions } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import Filter from "@insite/mobius/Icons/Filter";
import Printer from "@insite/mobius/Icons/Printer";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import ToasterContext, { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import HistoryContext, { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import downloadTearSheet from "@tSurya/Handlers/DownloadTearSheetHandler";
import generatePdf from "@tSurya/Handlers/GeneratePdfHandler";
import PrintIcon from "@tSurya/Icons/PrintIcon";
import { TYPOGRAPHY, COLORS } from "@tSurya/theme/design.surya";
import MyAccountThemeWrapper from "@tSurya/Utils/MyAccountThemeWrapper";
import * as React from "react";
import { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showOrderNumber = "showOrderNumber",
    showWebOrderNumber = "showWebOrderNumber",
    showDate = "showDate",
    showOrderTotal = "showOrderTotal",
    showStatus = "showStatus",
    showShipTo = "showShipTo",
    showPONumber = "showPONumber",
    showReorderProducts = "showReorderProducts",
}

type sortByField = {
    [key: string]: string[];
};

const sortByFields: sortByField = {
    webOrderNumber: ["webOrderNumber"],
    orderDate: ["orderDate", "erpOrderNumber", "webOrderNumber"],
    orderTotal: ["orderTotal"],
    status: ["status"],
    stCompanyName: ["stCompanyName"],
    customerPO: ["customerPO"],
    qtybackordered: ["qtybackordered"],
    qtyShipped: ["qtyShipped"],
    qtyOrdered: ["qtyOrdered"],
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showOrderNumber]: boolean;
        [fields.showWebOrderNumber]: boolean;
        [fields.showDate]: boolean;
        [fields.showOrderTotal]: boolean;
        [fields.showStatus]: boolean;
        [fields.showShipTo]: boolean;
        [fields.showPONumber]: boolean;
        [fields.showReorderProducts]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        parameter: state.pages.orderHistory.getOrdersParameter,
        isReordering: state.pages.orderHistory.isReordering,
        isUpdatingCart: state.context.isUpdatingCart,
        showAddToCartConfirmationDialog: getSettingsCollection(state).productSettings.showAddToCartConfirmationDialog,
        language: state.context.session.language,
        showPoNumber: getSettingsCollection(state).orderSettings.showPoNumber,
        isVmiOrderHistoryPage: state.pages.orderHistory.isVmiOrderHistoryPage,
        selectedOrderIds: state.pages.orderHistory.selectedOrderIds || {},
        showPricing: state.context.showPricing,
    };
};

const mapDispatchToProps = {
    updateSearchFields,
    reorder,
    selectOrders,
    exportOrders,
    generatePdf,
};

type Props = ReturnType<typeof mapStateToProps> &
    OwnProps &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface OrderHistoryTableStyles {
    container?: InjectableCss;
    headerClickable?: ClickableProps;
    headerText?: TypographyProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
    orderDateHeader?: DataTableHeaderProps;
    orderNumberHeader?: DataTableHeaderProps;
    shipToHeader?: DataTableHeaderProps;
    statusHeader?: DataTableHeaderProps;
    customerPOHeader?: DataTableHeaderProps;
    orderTotalHeader?: DataTableHeaderProps;
    reorderHeader?: DataTableHeaderProps;
    orderDateCells?: DataTableCellProps;
    orderNumberCells?: DataTableCellProps;
    shipToCells?: DataTableCellProps;
    statusCells?: DataTableCellProps;
    customerPOCells?: DataTableCellProps;
    orderTotalCells?: DataTableCellProps;
    reorderCells?: DataTableCellProps;
    reorderButton?: ButtonPresentationProps;
    reorderButtonSpinner?: LoadingSpinnerProps;
    orderNumberLink?: LinkPresentationProps;
    dataTable?: DataTableProps;
    checkboxHeader?: DataTableHeaderProps;
    checkboxCells?: DataTableCellProps;
    rowCheckbox?: CheckboxPresentationProps;
    headerContainer?: GridContainerProps;
    gridItem?: GridItemProps;
    exportLink?: LinkPresentationProps;
    button?: InjectableCss;
    tableRow?: InjectableCss;
    tableHeader?: InjectableCss;
    orderNumberStyle?: InjectableCss;
    toggleFilterButtonIcon?: ButtonIconPresentationProps;
    printIcon?: InjectableCss;
    qtyBackOrderCell?: InjectableCss;
}

export const orderHistoryTableStyles: OrderHistoryTableStyles = {
    button: {
        css: css`
            display: flex;
            align-items: center;
            padding: 0 36px;
            text-align: center;
            gap: 15px;
            border: 2px solid ${COLORS.text.accent};
            border: none;
            background-color: ${COLORS.primary.contrast};
            &:hover {
                background-color: #ffffff !important;
            }
            &:disabled {
                background-color: #ffffff !important;
            }
            span {
                font-family: ${TYPOGRAPHY.fontFamily.title};
                font-style: normal;
                font-weight: ${TYPOGRAPHY.weight.bold};
                font-size: ${TYPOGRAPHY.size.smaller};
                line-height: ${TYPOGRAPHY.lineHeight.xbase};
                color: ${COLORS.text.main};
                text-align: center;
                background-color: ${COLORS.primary.contrast};
            }
        `,
    },
    printIcon: {
        css: css`
            height: 19.25px;
            width: 22px;
            > svg {
                width: 100%;
                height: 100%;
            }
        `,
    },
    container: {
        css: css`
            overflow: auto;
            margin-top: 40px;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    noResultsContainer: {
        css: css`
            text-align: center;
            padding: 20px;
        `,
    },
    noResultsText: {
        variant: "h4",
        css: css`
            font-style: normal;
        `
    },
    headerClickable: {
        css: css`
            width: 100%;
            justify-content: space-between;
        `,
    },
    reorderButton: {
        color: "secondary",
        sizeVariant: "medium",
    },
    reorderButtonSpinner: {
        size: 22,
        css: css`
            margin-left: auto;
            margin-right: auto;
            display: block;
        `,
    },
    shipToHeader: {
        tight: true,
    },
    orderTotalHeader: {
        tight: true,
        alignX: "right",
    },
    reorderHeader: {
        tight: true,
    },
    orderTotalCells: {
        alignX: "right",
    },
    shipToCells: {
        typographyProps: {
            ellipsis: true,
            css: css`
                display: block;
                max-width: 300px;
            `,
        },
    },
    checkboxHeader: {
        tight: true,
    },
    headerContainer: {
        gap: 8,
        css: css`
            padding: 20px 0;
        `,
    },
    gridItem: {
        width: 12,
        css: css`
            > * {
                padding-right: 10px;
            }
        `,
    },
    exportLink: {
        css: css`
            margin-left: 10px;
        `,
    },
    orderNumberCells: {
        css: css`
            font-size: 15px;
            font-weight: bold;
        `,
    },
    tableRow: {
        css: css`
            td {
                background: #fff;
                border: 1px solid #e0e0e0;
            }
            tr {
                border: 1px solid #e0e0e0;
            }
            a {
                font-size: 15px !important;
            }
        `,
    },
    tableHeader: {
        css: css`
            th {
                border: 1px solid #e0e0e0;
            }
        `,
    },
    orderNumberStyle: {
        css: css`
            font-size: 15px;
            font-weight: bold;
        `,
    },
    toggleFilterButtonIcon: {
        src: Filter,
        size: 24,
    },
    qtyBackOrderCell: {
        css: css`
            font-size: 15px !important;
        `,
    },
    dataTable: {
        css: css`
            border: 1px solid ${COLORS.border.main};
            tr {
                > th {
                    width: 124px !important;
                    box-sizing: border-box;
                    height: 60px;
                    white-space: wrap !important;
                    padding: 0px;
                    min-width: 124px !important;
                    &.printColumn {
                        width: 59px !important;
                        padding: 0px;
                        gap: 0px;
                        min-width: 59px !important;
                        &:last-child {
                            span[class^="IconWrapper"] {
                                display: none;
                            }
                        }
                    }
                }
            }

            tr {
                > td {
                    width: 123px !important;
                    box-sizing: border-box;
                    height: 54px;
                    &.printColumn {
                        width: 59px !important;
                        padding: 0px !important;
                        > button {
                            padding: 0px !important;
                            gap: 0px;
                            width: 100%;
                            justify-content: center;
                        }
                    }
                    span {
                        white-space: normal;
                    }
                }
            }
        `,
    },
};

const styles = orderHistoryTableStyles;

interface State {
    // isLoading: boolean;
    isLoadingSpinner: boolean;
    newStates: { [key: string]: boolean };
}

class OrderHistoryTable extends React.Component<Props, State> {
    static contextType = OrdersDataViewContext;
    declare context: React.ContextType<typeof OrdersDataViewContext>;

    constructor(props: Props) {
        super(props);
        this.state = { isLoadingSpinner: false, newStates: { id: false } };
    }

    headerClick(sortField: string) {
        const previousSort = this.props.parameter.sort ?? "";
        const [previousPrimaryField] = previousSort.split(",");
        const direction = previousPrimaryField === sortField ? " DESC" : "";
        const sort = sortByFields[sortField].map(field => `${field}${direction}`).join(", ");
        this.props.updateSearchFields({ sort });
    }

    sorted = (sortField: string) => {
        let sorted: boolean | string = false;

        if (!this.props.parameter.sort) {
            return sorted;
        }
        const [primaryField] = this.props.parameter.sort.split(",");
        if (primaryField === sortField) {
            sorted = "ascending";
        } else if (primaryField === `${sortField} DESC`) {
            sorted = "descending";
        }
        return sorted as SortOrderOptions;
    };

    reorderClick = (orderNumber: string, linkOrderNumber: string, isVmiOrder: boolean) =>
        this.props.reorder({
            orderNumber,
            onSuccess: () => this.onReorderSuccess(orderNumber, linkOrderNumber, isVmiOrder),
            onError: message => this.onReorderError(message),
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    this.onSuccess?.();
                }
            },
        });
    onReorderError = (message: string) => {
        this.props.toaster.addToast({
            body: message,
            messageType: "danger",
            timeoutLength: 6000,
        });
    };

    onReorderSuccess = (orderNumber: string, linkOrderNumber: string, isVmiOrder: boolean) => {
        if (!this.props.showAddToCartConfirmationDialog) {
            return;
        }
        this.props.toaster.addToast({
            body: (
                <HistoryContext.Provider value={{ history: this.props.history }}>
                    <OrderDetailPageTypeLink
                        title={orderNumber}
                        orderNumber={linkOrderNumber}
                        isVmiOrder={isVmiOrder}
                        isVmiOrderDetailsPage={this.props.isVmiOrderHistoryPage}
                    />
                    &nbsp;{translate("Added to Cart")}
                </HistoryContext.Provider>
            ),
            messageType: "success",
            timeoutLength: 6000,
        });
    };

    setButtonAttribute = (event: any) => {
        if (event.target.tagName === "SPAN") {
            event.target.parentElement.setAttribute("disabled", "");
        } else {
            event.target.setAttribute("disabled", "");
        }
    };

    removeButtonAttribute = (event: any) => {
        if (event.target.tagName === "SPAN") {
            event.target.parentElement.removeAttribute("disabled", "");
        } else {
            event.target.removeAttribute("disabled", "");
        }
    };
    downloadTearsheetClickHandler = (orderNumber: string, event: any, id: string) => {
        this.state.newStates[orderNumber] = true;
        if (typeof window !== undefined) {
            this.setState({ isLoadingSpinner: this.state.newStates[orderNumber] });
            // this.setButtonAttribute(event);
            this.props.generatePdf({
                identifier: orderNumber,
                templateName: "order",
                onSuccess: (result: any) => {
                    this.state.newStates[orderNumber] = false;
                    this.setState({ isLoadingSpinner: this.state.newStates[orderNumber] });
                    // this.removeButtonAttribute(event);
                },
                onError: (result: any) => {
                    // this.removeButtonAttribute(event);
                    this.props.toaster.addToast({
                        body: translate(`${result}`),
                    });
                },
            });
        }
    };

    render() {
        const ordersDataView = this.context;

        if (ordersDataView.isLoading) {
            return (
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner {...styles.spinner} />
                </StyledWrapper>
            );
        }

        if (!ordersDataView.value) {
            return null;
        }

        if (ordersDataView.value.length === 0) {
            return (
                <StyledWrapper {...styles.noResultsContainer}>
                    <Typography as="p" {...styles.noResultsText} data-test-selector="orderHistoryTable_noOrdersFound">
                        {translate("No orders found")}
                    </Typography>
                </StyledWrapper>
            );
        }

        const rows = ordersDataView.value.map(order => {
            return {
                id: order.id,
                linkOrderNumber: order.erpOrderNumber || order.webOrderNumber,
                date: order.orderDate
                    ? new Date(order.orderDate).toLocaleDateString("en-US", {
                          month: "2-digit",
                          day: "2-digit",
                          year: "numeric",
                      })
                    : "",
                orderNumber: order.erpOrderNumber,
                webOrderNumber: order.webOrderNumber,
                shipTo: `${order.stCompanyName} ${order.stAddress1} ${order.stAddress2} ${order.shipToCity} ${order.shipToState}`,
                status: order.statusDisplay,
                po: order.customerPO,
                total: order.orderGrandTotalDisplay,
                isVmiOrder: !!order.vmiLocationId,
                qtyBackOrdered: order.properties.qtyBackOrdered,
                qtyOrdered: order.properties.qtyOrdered,
                qtyShipped: order.properties.qtyShipped,
            };
        });

        const {
            showOrderNumber,
            showWebOrderNumber,
            showDate,
            showOrderTotal,
            showStatus,
            showShipTo,
            showPONumber,
            showReorderProducts,
        } = this.props.fields;
        // const [isLoadingSpinner, setisLoadingSpinner] = useState(true);
        const isTrueOrUndefined = (value?: boolean) => value === true || value === undefined;
        const checkboxChangeHandler = (value: boolean, id?: string) => {
            if (id) {
                this.props.selectOrders({ ids: [id] });
            } else {
                let ids: string[] = [];
                if (value) {
                    for (const row of this.context.value!) {
                        if (!this.props.selectedOrderIds[row.id]) {
                            ids.push(row.id);
                        }
                    }
                } else {
                    ids = Object.keys(this.props.selectedOrderIds);
                }

                this.props.selectOrders({ ids });
            }
        };

        const handleExportButtonClick = (selectedOnly = false) => {
            this.props.exportOrders({ ids: selectedOnly ? this.props.selectedOrderIds : {} });
        };

        return (
            <MyAccountThemeWrapper>
                <StyledWrapper {...styles.container} data-test-selector="orderHistoryTable">
                {rows.length > 0 && this.props.isVmiOrderHistoryPage && (
                    <GridContainer {...styles.headerContainer}>
                        <GridItem {...styles.gridItem}>
                            <>
                                <Link
                                    disabled={Object.keys(this.props.selectedOrderIds).length === 0}
                                    onClick={() => handleExportButtonClick(true)}
                                >
                                    {translate("Export Selected")}
                                </Link>
                                <Link {...styles.exportLink} onClick={() => handleExportButtonClick()}>
                                    {translate("Export All")}
                                </Link>
                            </>
                        </GridItem>
                    </GridContainer>
                )}
                <DataTable {...styles.dataTable}>
                    <DataTableHead {...styles.tableHeader}>
                        {this.props.isVmiOrderHistoryPage && (
                            <DataTableHeader {...styles.checkboxHeader}>
                                <Checkbox
                                    {...styles.rowCheckbox}
                                    checked={
                                        Object.keys(this.props.selectedOrderIds).length === ordersDataView.value.length
                                    }
                                    onChange={(e, value) => checkboxChangeHandler(value)}
                                />
                            </DataTableHeader>
                        )}
                        {isTrueOrUndefined(showOrderNumber) && (
                            <DataTableHeader
                                {...styles.orderNumberHeader}
                                title={translate("Order Number")}
                                sorted={this.sorted("webOrderNumber")}
                                onSortClick={() => this.headerClick("webOrderNumber")}
                                data-test-selector="tableHeader_webOrderNumber"
                            >
                                {translate("Order No.", "webOrderNumber")}
                            </DataTableHeader>
                        )}
                        {isTrueOrUndefined(showPONumber) && this.props.showPoNumber && (
                            <DataTableHeader
                                {...styles.customerPOHeader}
                                title={translate("Purchase Order Number")}
                                sorted={this.sorted("customerPO")}
                                onSortClick={() => this.headerClick("customerPO")}
                            >
                                {translate("P.O. No.", "customerPO")}
                            </DataTableHeader>
                        )}
                        {isTrueOrUndefined(showDate) && (
                            <DataTableHeader
                                {...styles.orderDateHeader}
                                sorted={this.sorted("orderDate")}
                                onSortClick={() => this.headerClick("orderDate")}
                                data-test-selector="tableHeader_OrderDate"
                            >
                                {translate("Order Date", "orderDate")}
                            </DataTableHeader>
                        )}
                        {isTrueOrUndefined(showOrderTotal) && this.props.showPricing && (
                            <DataTableHeader
                                {...styles.orderTotalHeader}
                                sorted={this.sorted("orderTotal")}
                                onSortClick={() => this.headerClick("orderTotal")}
                            >
                                {translate("Order Total", "orderTotal")}
                            </DataTableHeader>
                        )}
                        {/* {isTrueOrUndefined(showShipTo) && (
                            <DataTableHeader
                                {...styles.shipToHeader}
                                sorted={this.sorted("stCompanyName")}
                                onSortClick={() => this.headerClick("stCompanyName")}
                            >
                                {translate("Ship To / Pick Up", "stCompanyName")}
                            </DataTableHeader>
                        )} */}
                        <DataTableHeader
                            {...styles.statusHeader}
                            sorted={this.sorted("qtyOrdered")}
                            onSortClick={() => this.headerClick("qtyOrdered")}
                            data-test-selector="QTY Ordered"
                        >
                            {translate("Ordered")}
                        </DataTableHeader>
                        <DataTableHeader
                            {...styles.statusHeader}
                            sorted={this.sorted("qtyShipped")}
                            onSortClick={() => this.headerClick("qtyShipped")}
                            data-test-selector="tableHeader_QTY_Shipped"
                        >
                            {translate("Shipped")}
                        </DataTableHeader>
                        <DataTableHeader
                            {...styles.statusHeader}
                            sorted={this.sorted("qtybackordered")}
                            onSortClick={() => this.headerClick("qtybackordered")}
                            data-test-selector="Backorder"
                        >
                            {translate("Backorder")}
                        </DataTableHeader>
                        {isTrueOrUndefined(showStatus) && (
                            <DataTableHeader
                                {...styles.statusHeader}
                                sorted={this.sorted("status")}
                                onSortClick={() => this.headerClick("status")}
                                data-test-selector="tableHeader_status"
                            >
                                {translate("Status", "status")}
                            </DataTableHeader>
                        )}
                        {/* {isTrueOrUndefined(showWebOrderNumber) && (
                            <DataTableHeader
                                {...styles.orderNumberHeader}
                                title={translate("Order Number")}
                                sorted={this.sorted("webOrderNumber")}
                                onSortClick={() => this.headerClick("webOrderNumber")}
                            >
                                {translate("WebOrderId")}
                            </DataTableHeader>
                        )}
                        {isTrueOrUndefined(showReorderProducts)  && (
                            <DataTableHeader {...styles.reorderHeader} title={translate("Reorder")}>
                                {translate("Reorder")}
                            </DataTableHeader>
                        )} */}
                        <DataTableHeader tight className="printColumn">
                            {translate("Print")}
                        </DataTableHeader>
                    </DataTableHead>
                    <DataTableBody data-test-selector="orderHistoryTable_tableBody">
                        {rows.map(
                            ({
                                id,
                                linkOrderNumber,
                                webOrderNumber,
                                date,
                                orderNumber,
                                shipTo,
                                status,
                                po,
                                total,
                                isVmiOrder,
                                qtyBackOrdered,
                                qtyOrdered,
                                qtyShipped,
                            }) => (
                                <DataTableRow key={id} {...styles.tableRow}>
                                    {this.props.isVmiOrderHistoryPage && (
                                        <DataTableCell {...styles.checkboxCells}>
                                            <Checkbox
                                                {...styles.rowCheckbox}
                                                checked={this.props.selectedOrderIds[id]}
                                                onChange={(e, value) => checkboxChangeHandler(value, id)}
                                            />
                                        </DataTableCell>
                                    )}
                                    {isTrueOrUndefined(showOrderNumber) && (
                                        <DataTableCell
                                            {...styles.orderNumberCells}
                                            data-test-selector="orderHistoryTable_tableCell_orderNumber"
                                        >
                                            <OrderDetailPageTypeLink
                                                {...styles.orderNumberStyle}
                                                title={orderNumber}
                                                orderNumber={linkOrderNumber}
                                                isVmiOrder={isVmiOrder}
                                                isVmiOrderDetailsPage={this.props.isVmiOrderHistoryPage}
                                            />
                                        </DataTableCell>
                                    )}
                                    {isTrueOrUndefined(showPONumber) && this.props.showPoNumber && (
                                        <DataTableCell {...styles.customerPOCells}>{po}</DataTableCell>
                                    )}
                                    {isTrueOrUndefined(showDate) && (
                                        <DataTableCell
                                            {...styles.orderDateCells}
                                            data-test-selector="orderHistoryTable_tableCell_date"
                                        >
                                            {date}
                                        </DataTableCell>
                                    )}
                                    {isTrueOrUndefined(showOrderTotal) && this.props.showPricing && (
                                        <DataTableCell {...styles.orderTotalCells}>{total}</DataTableCell>
                                    )}

                                    {/* {isTrueOrUndefined(showShipTo) && (
                                        <DataTableCell {...styles.shipToCells}>{shipTo}</DataTableCell>
                                    )} */}
                                    <DataTableCell {...styles.shipToCells}>{qtyOrdered}</DataTableCell>
                                    <DataTableCell {...styles.shipToCells}>{qtyShipped}</DataTableCell>
                                    <DataTableCell {...styles.qtyBackOrderCell}>
                                        {Math.round(Number(qtyBackOrdered))}
                                    </DataTableCell>
                                    {isTrueOrUndefined(showStatus) && (
                                        <DataTableCell
                                            {...styles.statusCells}
                                            data-test-selector="orderHistoryTable_tableCell_status"
                                        >
                                            {status}
                                        </DataTableCell>
                                    )}
                                    {/* {isTrueOrUndefined(showWebOrderNumber) && (
                                        <DataTableCell
                                            {...styles.orderNumberCells}
                                            data-test-selector="orderHistoryTable_tableCell_orderNumber"
                                        >
                                            {webOrderNumber}
                                        </DataTableCell>
                                    )} */}
                                    {/* {isTrueOrUndefined(showReorderProducts)  && (
                                        <DataTableCell {...styles.reorderCells}>
                                            {this.props.isReordering[orderNumber] ? (
                                                <LoadingSpinner {...styles.reorderButtonSpinner} />
                                            ) : (
                                                <Button
                                                    disabled={
                                                        Object.keys(this.props.isReordering).length > 0 ||
                                                        this.props.isUpdatingCart
                                                    }
                                                    {...styles.reorderButton}
                                                    onClick={() =>
                                                        this.reorderClick(orderNumber, linkOrderNumber, isVmiOrder)
                                                    }
                                                    data-test-selector="orderHistoryTable_Reorder"
                                                >
                                                    {translate("Reorder")}
                                                </Button>
                                            )}
                                        </DataTableCell>
                                    )} */}
                                    <DataTableCell className="printColumn">
                                        {this.state.newStates[id] ? (
                                            <LoadingSpinner {...styles.reorderButtonSpinner} />
                                        ) : (
                                            <Button
                                                {...styles.button}
                                                onClick={event =>
                                                    this.downloadTearsheetClickHandler(orderNumber, event, id)
                                                }
                                            >
                                                <StyledWrapper>
                                                    <Icon src={Printer} {...styles.printIcon} />
                                                </StyledWrapper>
                                            </Button>
                                            // 	<Button
                                            //     {...styles.printIcon}
                                            //     onClick={event => this.downloadTearsheetClickHandler(orderNumber, event, id)}
                                            // >
                                            //      <PrintIcon />
                                            // </Button>
                                        )}
                                    </DataTableCell>
                                </DataTableRow>
                            ),
                        )}
                    </DataTableBody>
                </DataTable>
            </StyledWrapper>
            </MyAccountThemeWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(OrderHistoryTable))),
    definition: {
        group: "Order History",
        displayName: "Search Results Table",
        allowedContexts: ["OrderHistoryPage", "VmiOrderHistoryPage"],
        fieldDefinitions: [
            {
                name: fields.showOrderNumber,
                displayName: "Order #",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "General",
                sortOrder: 0,
            },
            {
                name: fields.showWebOrderNumber,
                displayName: "Order #",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 0,
            },
            {
                name: fields.showDate,
                displayName: "Date",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.showOrderTotal,
                displayName: "Order Total",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.showStatus,
                displayName: "Status",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showShipTo,
                displayName: "Ship To/Pick Up",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showPONumber,
                displayName: "PO #",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.showReorderProducts,
                displayName: "Reorder",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 6,
            },
        ],
    },
};

export default widgetModule;
